import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Highlighter, Eye, Envelope, DeviceMobile, Sparkle, FolderSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { KAT_LABEL, KAT_COLOR, STATUS, statusOf, fmtSize, fmtDate, DocIcon, Thumb } from "./dmsUtils";

function StatusDot({ d }) {
  const s = STATUS.find(x => x.key === statusOf(d)) || STATUS[0];
  return <span title={s.label} className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: s.dot }} data-testid={`status-dot-${d.id}`} />;
}

function Flags({ d }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[#9CA3AF]">
      {d.favorit && <Star size={13} weight="fill" className="text-[#F59E0B]" />}
      {(d.annotationen || 0) > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-[#1A2238] font-medium" title="Markierungen"><Highlighter size={12} weight="duotone" />{d.annotationen}</span>}
      {d.mandant_sichtbar && <Eye size={13} weight="duotone" className="text-[#15803D]" title="Für Mandant sichtbar" />}
      {d.source === "imap" && <Envelope size={13} title="Per E-Mail eingegangen" />}
      {d.source === "portal" && <DeviceMobile size={13} className="text-[#15803D]" title="Vom Mandanten hochgeladen" />}
      {d.analyzed && <Sparkle size={13} weight="duotone" className="text-[#1A2238]" title="KI ausgewertet" />}
      {(d.versionen_anzahl || 0) > 0 && <span className="inline-flex items-center gap-0.5 text-[10px]" title="Versionen"><ClockCounterClockwise size={12} />v{(d.versionen_anzahl || 0) + 1}</span>}
    </span>
  );
}

export default function DmsList({ docs, view, selected, onToggle, onToggleAll, activeId, onOpen, hits, onStar }) {
  const allSelected = docs.length > 0 && docs.every(d => selected.has(d.id));
  if (docs.length === 0) {
    return <div className="p-12 text-center text-sm text-[#6B7280]" data-testid="dms-empty">Keine Dokumente in dieser Ansicht.</div>;
  }

  if (view === "grid") {
    return (
      <div className="p-3" data-testid="dms-grid">
        <label className="flex items-center gap-2 text-xs text-[#6B7280] px-1 pb-2 cursor-pointer w-max">
          <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="dms-select-all" /> Alle auswählen
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {docs.map(d => {
            const sel = selected.has(d.id);
            return (
              <div key={d.id} onClick={() => onOpen(d)} data-testid={`dms-tile-${d.id}`}
                className={`group relative bg-white border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${activeId === d.id ? "border-[#1A2238] ring-1 ring-[#1A2238]" : sel ? "border-[#1A2238]/50" : "border-[#E5E7EB]"}`}>
                <Thumb d={d} className="aspect-[4/3]" />
                <div className="absolute top-2 left-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={sel} onCheckedChange={() => onToggle(d.id)} className={`bg-white ${sel ? "" : "opacity-0 group-hover:opacity-100"}`} data-testid={`dms-check-${d.id}`} />
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); onStar(d); }} className={`absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center ${d.favorit ? "text-[#F59E0B]" : "text-[#9CA3AF] opacity-0 group-hover:opacity-100"}`} data-testid={`dms-star-${d.id}`}>
                  <Star size={15} weight={d.favorit ? "fill" : "regular"} />
                </button>
                <div className="p-2.5 space-y-1.5">
                  <div className="flex items-start gap-1.5">
                    <StatusDot d={d} />
                    <p className="text-xs font-medium text-[#0A192F] leading-snug line-clamp-2 flex-1 -mt-0.5" title={d.original_filename}>{d.original_filename}</p>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${KAT_COLOR[d.kategorie] || "bg-[#F3F4F6] text-[#4B5563]"}`}>{KAT_LABEL[d.kategorie] || d.kategorie}</span>
                    <Flags d={d} />
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] truncate">{d.akte ? `${d.akte.aktenzeichen}${d.akte.mandant_name ? ` · ${d.akte.mandant_name}` : ""}` : "Ohne Akte"} · {fmtDate(d.created_at, false)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dms-list">
      <div className="grid grid-cols-[28px_minmax(0,1fr)] md:grid-cols-[28px_minmax(0,1fr)_160px_130px_90px_70px] items-center gap-2 px-3 h-9 border-b border-[#E5E7EB] text-[10px] uppercase tracking-wider text-[#9CA3AF] sticky top-0 bg-white z-10">
        <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="dms-select-all" />
        <span>Name</span>
        <span className="hidden md:block">Akte</span>
        <span className="hidden md:block">Kategorie</span>
        <span className="hidden md:block">Datum</span>
        <span className="hidden md:block text-right">Größe</span>
      </div>
      <ul className="divide-y divide-[#F3F4F6]">
        {docs.map(d => {
          const sel = selected.has(d.id);
          const hit = hits?.[d.id];
          return (
            <li key={d.id} onClick={() => onOpen(d)} data-testid={`dms-row-${d.id}`}
              className={`grid grid-cols-[28px_minmax(0,1fr)] md:grid-cols-[28px_minmax(0,1fr)_160px_130px_90px_70px] items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${activeId === d.id ? "bg-[#1A2238]/5" : sel ? "bg-[#FFFBEB]" : "hover:bg-[#FAFAFA]"}`}>
              <span onClick={(e) => e.stopPropagation()}><Checkbox checked={sel} onCheckedChange={() => onToggle(d.id)} data-testid={`dms-check-${d.id}`} /></span>
              <div className="flex items-center gap-2.5 min-w-0">
                <DocIcon d={d} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <StatusDot d={d} />
                    <p className="text-sm font-medium text-[#0A192F] truncate min-w-0 flex-1" data-testid={`dms-name-${d.id}`}>{d.original_filename}</p>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onStar(d); }} className={`shrink-0 ${d.favorit ? "text-[#F59E0B]" : "text-[#D1D5DB] hover:text-[#F59E0B]"}`} data-testid={`dms-star-${d.id}`}>
                      <Star size={13} weight={d.favorit ? "fill" : "regular"} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-[#6B7280] min-w-0 flex-wrap">
                    <Flags d={d} />
                    {(d.tags || []).slice(0, 3).map(t => <span key={t} className="text-[10px] text-[#1A2238] bg-[#1A2238]/5 rounded px-1">#{t}</span>)}
                    <span className={`md:hidden text-[10px] px-1.5 py-0.5 rounded ${KAT_COLOR[d.kategorie] || "bg-[#F3F4F6] text-[#4B5563]"}`}>{KAT_LABEL[d.kategorie] || d.kategorie}</span>
                    <span className="md:hidden truncate">{d.akte ? d.akte.aktenzeichen : "Ohne Akte"} · {fmtDate(d.created_at, false)} · {fmtSize(d.size)}</span>
                  </div>
                  {hit && <p className="text-[11px] text-[#4B5563] mt-0.5 truncate" data-testid={`dms-hit-${d.id}`}><span className="text-[#D32F2F] font-medium">{hit.treffer}× </span>{hit.snippet}</p>}
                </div>
              </div>
              <span className="hidden md:flex items-center gap-1 text-xs font-mono text-[#1A2238] truncate">
                {d.akte ? <><FolderSimple size={13} weight="duotone" /> <span className="truncate">{d.akte.aktenzeichen}{d.akte.mandant_name ? ` · ${d.akte.mandant_name}` : ""}</span></> : <span className="text-[#9CA3AF] font-sans">{d.akte_geloescht ? "Akte gelöscht" : "—"}</span>}
              </span>
              <span className={`hidden md:block text-[11px] px-1.5 py-0.5 rounded w-max max-w-full truncate ${KAT_COLOR[d.kategorie] || "bg-[#F3F4F6] text-[#4B5563]"}`}>{KAT_LABEL[d.kategorie] || d.kategorie}</span>
              <span className="hidden md:block text-xs text-[#6B7280] font-mono">{fmtDate(d.created_at, false)}</span>
              <span className="hidden md:block text-xs text-[#6B7280] font-mono text-right">{fmtSize(d.size)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
